import { Mail, RotateCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Order, OrderNotification } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useOrderNotifications } from '../hooks/use-order-notifications';
import { RESENDABLE_EMAILS } from '../schemas/order-rules';

import { ResendEmailDialog } from './resend-email-dialog';

const STATUS_STYLES: Record<OrderNotification['status'], string> = {
  queued: 'bg-warning/20 text-warning-foreground border-transparent dark:bg-warning/25',
  sent: 'bg-success/15 text-success border-transparent dark:bg-success/20',
  failed: 'bg-destructive/10 text-destructive border-transparent',
};

export interface OrderNotificationsCardProps {
  storeId: string;
  order: Pick<Order, 'id' | 'name' | 'email'>;
  /** `orders.write`: the resend action is offered on every email the API can send again. */
  canResend: boolean;
}

/**
 * Emails the worker sent about this order, with a resend for each kind. A failed one shows its
 * error so support can act; a resend is marked as such.
 */
export function OrderNotificationsCard({ storeId, order, canResend }: OrderNotificationsCardProps) {
  const { t } = useTranslation('orders');
  const format = useFormat();
  const query = useOrderNotifications(storeId, order.id);
  const [resending, setResending] = useState<string | null>(null);

  return (
    <Card className="gap-3">
      <CardHeader>
        <CardTitle>{t('notifications.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {query.isPending ? (
          <Skeleton className="h-12" />
        ) : query.error ? (
          <p className="text-destructive text-sm">{t('notifications.error')}</p>
        ) : query.data.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('notifications.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-2 text-sm" aria-label={t('notifications.title')}>
            {query.data.map((notification) => {
              const name = t(`notifications.event.${notification.event}`, {
                defaultValue: notification.subject ?? notification.event,
              });
              return (
                <li key={notification.id} className="flex items-start gap-2">
                  <Mail className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{name}</p>
                    <p className="text-muted-foreground truncate text-xs">
                      {notification.recipient}
                      {' · '}
                      <span title={format.utc(notification.sentAt ?? notification.createdAt)}>
                        {format.relative(notification.sentAt ?? notification.createdAt)}
                      </span>
                      {notification.resentBy ? ` · ${t('notifications.resent')}` : null}
                    </p>
                    {notification.error ? (
                      <p className="text-destructive text-xs">{notification.error}</p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Badge variant="outline" className={cn(STATUS_STYLES[notification.status])}>
                      {t(`notifications.status.${notification.status}`)}
                    </Badge>
                    {canResend && RESENDABLE_EMAILS.has(notification.event) ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        aria-label={t('resend.action', { email: name })}
                        onClick={() => setResending(notification.event)}
                      >
                        <RotateCw aria-hidden />
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
      {resending ? (
        <ResendEmailDialog
          storeId={storeId}
          order={order}
          templateKey={resending}
          onOpenChange={(open) => {
            if (!open) setResending(null);
          }}
        />
      ) : null}
    </Card>
  );
}
